import { eventRange } from '@/components/enc/format';
import { PageHeader, Pill } from '@/components/enc/primitives';
import { Button } from '@/components/ui/button';
import { ContentLoader } from '@/components/ui/loaders';
import { useToast } from '@/hooks/use-toast';
import { date } from '@/lib/date';
import { printCoupleList, printFicha } from '@/lib/print/print-actions';
import { usePrintPreview } from '@/lib/print/use-print-preview';
import { useEvent, useEvents, type EventWithCount } from '@/services/events';
import { Calendar } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const statusOf = (e: EventWithCount, nextId?: string) => {
  if (!e.isActive) return { label: 'Inativo', tone: 'neutral' as const };
  if (!date(e.endDate).isBefore(date().startOf('day')))
    return {
      label: e.id === nextId ? 'Inscrições abertas' : 'Próximo',
      tone: 'brand' as const,
    };
  return { label: 'Realizado', tone: 'neutral' as const };
};

export const Page = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const print = usePrintPreview();
  const { data, isLoading } = useEvents({
    perPage: 100,
    orderBy: 'startDate',
    order: 'desc',
  });
  const { data: nextEvent } = useEvent('next');

  const handleList = async (e: EventWithCount) => {
    try {
      await printCoupleList(print.open, e);
    } catch {
      toast.error('Não foi possível carregar os casais do encontro.');
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <ContentLoader show={isLoading} />

      <PageHeader
        eyebrow="CADASTROS"
        title="Encontros"
        description="Edições do retiro. O encontro ativo mais próximo recebe as inscrições do site."
        actions={
          <Button onClick={() => navigate('/admin/events/add')}>Novo encontro</Button>
        }
      />

      <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]">
        {(data?.data ?? []).map((e) => {
          const status = statusOf(e, nextEvent?.id);
          return (
            <article
              key={e.id}
              className="flex flex-col gap-5 rounded-[18px] border border-line bg-surface p-6 transition-colors hover:border-line-2"
            >
              <div className="flex items-start justify-between gap-3">
                <span className="font-display text-[44px] leading-none font-extrabold tracking-[-0.04em] text-ink">
                  {date(e.startDate).year()}
                </span>
                <Pill tone={status.tone}>{status.label}</Pill>
              </div>
              <div className="flex flex-col gap-2">
                <span className="font-display text-[17px] font-bold text-ink">
                  {e.description}
                </span>
                <span className="flex items-center gap-2 text-[13px] text-muted">
                  <Calendar className="size-[15px]" />
                  {eventRange(e)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3 border-t border-line pt-4">
                <Link
                  to={`/admin/couples?eventId=${e.id}`}
                  className="flex items-baseline gap-1.5 text-body"
                >
                  <strong className="font-display text-xl text-ink">
                    {e._count?.couples ?? 0}
                  </strong>
                  <span className="text-[13px] text-muted">casais</span>
                </Link>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/admin/events/${e.id}/edit`)}
                >
                  Editar
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => printFicha(print.open, e)}
                >
                  Imprimir ficha
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleList(e)}>
                  Lista de casais
                </Button>
              </div>
            </article>
          );
        })}
      </div>

      {!isLoading && data?.data.length === 0 && (
        <div className="rounded-[18px] border border-line bg-surface p-12 text-center text-sm text-muted">
          Nenhum encontro cadastrado.
        </div>
      )}

      {print.dialog}
    </div>
  );
};

export default Page;
